import { useState, useEffect, useCallback, useRef } from 'react';

interface AudioState {
  isMuted: boolean;
  volume: number;
  isPlaying: boolean;
}

interface UseAudioResult extends AudioState {
  toggleMute: () => void;
  setVolume: (volume: number) => void;
  updateGeneration: (powerMW: number, peakPowerMW: number) => void;
  updateZoom: (zoom: number) => void;
}

// Create audio context and oscillators for the inverter hum
class InverterAudio {
  private audioContext: AudioContext | null = null;
  private oscillator: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private noiseGain: GainNode | null = null;
  private isInitialized = false;
  private baseVolume = 0.05; // Very quiet by default
  private targetGain = 0;
  private muted = true; // Start muted by default

  initialize() {
    if (this.isInitialized) return;
    
    try {
      this.audioContext = new AudioContext();
      
      // Main oscillator for the base hum (60Hz for UK grid frequency feel)
      this.oscillator = this.audioContext.createOscillator();
      this.oscillator.type = 'sine';
      this.oscillator.frequency.value = 60;
      
      // Gain node for volume control
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 0;
      
      // Connect oscillator -> gain -> output
      this.oscillator.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      this.oscillator.start();
      
      // Create subtle noise for texture (clicks/rattles)
      this.createNoiseTexture();
      
      this.isInitialized = true;
    } catch (err) {
      console.warn('Web Audio API not supported:', err);
    }
  }

  private createNoiseTexture() {
    if (!this.audioContext) return;
    
    // Create a buffer with subtle noise
    const bufferSize = this.audioContext.sampleRate * 2;
    const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    
    // Generate sparse clicks (very occasional)
    for (let i = 0; i < bufferSize; i++) {
      // Very sparse clicks
      if (Math.random() < 0.0001) {
        output[i] = (Math.random() - 0.5) * 0.3;
      } else {
        output[i] = 0;
      }
    }
    
    this.noiseGain = this.audioContext.createGain();
    this.noiseGain.gain.value = 0;
    this.noiseGain.connect(this.audioContext.destination);
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    if (!this.gainNode || !this.audioContext) return;
    
    if (muted) {
      this.gainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.1);
      if (this.noiseGain) {
        this.noiseGain.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.1);
      }
    } else {
      this.gainNode.gain.setTargetAtTime(this.targetGain, this.audioContext.currentTime, 0.1);
    }
  }

  setBaseVolume(volume: number) {
    this.baseVolume = Math.max(0, Math.min(0.15, volume)); // Cap at 15%
    this.updateGain();
  }

  updateGeneration(powerMW: number, peakPowerMW: number) {
    if (peakPowerMW <= 0) {
      this.targetGain = 0;
    } else {
      // Scale volume with generation (more power = slightly louder)
      const ratio = Math.min(1, powerMW / peakPowerMW);
      this.targetGain = this.baseVolume * (0.3 + ratio * 0.7);
    }
    this.updateGain();
  }

  updateZoom(zoom: number) {
    // Adjust pitch slightly with zoom (closer = higher frequency harmonics)
    if (!this.oscillator || !this.audioContext) return;
    
    // Subtle frequency variation based on zoom (10-18 typical Leaflet zoom range)
    const normalizedZoom = Math.min(1, Math.max(0, (zoom - 10) / 8));
    const frequency = 60 + normalizedZoom * 30; // 60-90 Hz range
    
    this.oscillator.frequency.setTargetAtTime(
      frequency,
      this.audioContext.currentTime,
      0.5
    );
  }

  private updateGain() {
    if (!this.gainNode || !this.audioContext) return;
    
    // Only update gain if not muted
    const actualGain = this.muted ? 0 : this.targetGain;
    this.gainNode.gain.setTargetAtTime(
      actualGain,
      this.audioContext.currentTime,
      0.3
    );
  }

  resume() {
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  destroy() {
    if (this.oscillator) {
      this.oscillator.stop();
      this.oscillator.disconnect();
    }
    if (this.gainNode) {
      this.gainNode.disconnect();
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.isInitialized = false;
  }
}

export function useAudio(): UseAudioResult {
  const [state, setState] = useState<AudioState>({
    isMuted: true,
    volume: 0.05,
    isPlaying: false,
  });
  
  const audioRef = useRef<InverterAudio | null>(null);

  useEffect(() => {
    audioRef.current = new InverterAudio();
    
    // Initialize on first user interaction
    const handleInteraction = () => {
      audioRef.current?.initialize();
      audioRef.current?.resume();
      // Ensure audio starts muted
      audioRef.current?.setMuted(true);
      setState(s => ({ ...s, isPlaying: true }));
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    
    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    
    return () => {
      audioRef.current?.destroy();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  const toggleMute = useCallback(() => {
    setState(s => {
      const newMuted = !s.isMuted;
      audioRef.current?.setMuted(newMuted);
      return { ...s, isMuted: newMuted };
    });
  }, []);

  const setVolume = useCallback((volume: number) => {
    setState(s => ({ ...s, volume }));
    audioRef.current?.setBaseVolume(volume);
  }, []);

  const updateGeneration = useCallback((powerMW: number, peakPowerMW: number) => {
    audioRef.current?.updateGeneration(powerMW, peakPowerMW);
  }, []);

  const updateZoom = useCallback((zoom: number) => {
    audioRef.current?.updateZoom(zoom);
  }, []);

  return {
    ...state,
    toggleMute,
    setVolume,
    updateGeneration,
    updateZoom,
  };
}

